/**
 * @file index.tsx
 * @module index
 * @description Application entry point and React DOM render.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import { createRoot } from 'react-dom/client';
// Axios
import axios from 'axios';
import { Chart, registerables } from 'chart.js';
import { QueryClient, QueryClientProvider } from 'react-query';
// import { ReactQueryDevtools } from 'react-query/devtools'
// Apps
import { UTradeAdminI18nProvider } from './_admin/i18n/UTradeAdmin18n';
/**
 * TIP: Replace this style import with rtl styles to enable rtl mode
 *
 * import './_admin/assets/css/style.rtl.css'
 **/
import 'sweetalert2/dist/sweetalert2.min.css';
import './_admin/assets/sass/style.scss';
import './_admin/assets/sass/plugins.scss';
import './_admin/assets/sass/style.react.scss';
import './app/styles/components/GlobalButtons.css';
import './app/modules/_common/styles/global.css';
import { AppRoutes } from './app/routing/AppRoutes';
import { AuthProvider } from './app/modules/auth';
import { WarehouseProvider } from './app/context/WarehouseContext';
import { ToastContainer } from 'react-toastify';
import { Provider } from 'react-redux';
import 'react-toastify/dist/ReactToastify.css';
// import setupLocatorUI from '@locator/runtime';
import store from './redux/sellerStore';
import { SocketProvider } from './app/modules/socket/core/Socket';
import { PostHogProvider } from '@posthog/react'
import posthog from 'posthog-js';
import { POSTHOG_ENABLED_CACHE_KEY, PUBLIC_POSTHOG_HOST, PUBLIC_POSTHOG_KEY } from './utils/constants';

const options = {
  api_host: PUBLIC_POSTHOG_HOST,
  defaults: '2026-01-30',
} as const

// setupAxios(axios)
Chart.register(...registerables);

// Browsers report "ResizeObserver loop completed with undelivered notifications" when a resize
// callback changes layout again. It is harmless, but the development error overlay shows it as a
// crash. Keep it out of the overlay (development only; real errors still show).
if (process.env.NODE_ENV === 'development') {
  window.addEventListener(
    'error',
    (event) => {
      if (/ResizeObserver loop/.test(event.message)) event.stopImmediatePropagation();
    },
    true
  );
}
const queryClient = new QueryClient();
const container = document.getElementById('root');
const isLocalhost = ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
const isPosthogEnabled = localStorage.getItem(POSTHOG_ENABLED_CACHE_KEY) !== 'false';

// Build the client ourselves (instead of letting PostHogProvider construct it) so an internal
// posthog-js initialization failure (e.g. a browser extension mangling globals) doesn't crash
// the whole app render.
const posthogClient = (() => {
  if (isLocalhost || !isPosthogEnabled) {
    return null;
  }
  try {
    return posthog.init(PUBLIC_POSTHOG_KEY, options) ?? null;
  } catch (error) {
    console.error('PostHog failed to initialize, continuing without analytics.', error);
    return null;
  }
})();
// setupLocatorUI();
if (container) {
  const app = (
    <QueryClientProvider client={queryClient}>
      <UTradeAdminI18nProvider>
        <AuthProvider>
          <SocketProvider>
            <WarehouseProvider>
              <ToastContainer />
              <Provider store={store}>
                <AppRoutes />
              </Provider>
            </WarehouseProvider>
          </SocketProvider>
        </AuthProvider>
      </UTradeAdminI18nProvider>
      {/* <ReactQueryDevtools initialIsOpen={false} /> */}
    </QueryClientProvider>
  );

  createRoot(container).render(
    posthogClient
      ? <PostHogProvider client={posthogClient}>{app}</PostHogProvider>
      : app
  );
}
